import React from 'react'
import Loading from '../../../../../components/loaders/Loading'
import { Button, Checkbox, Divider, Row, Space } from 'antd'
import { EyeInvisibleOutlined, EyeOutlined, DownOutlined, RightOutlined } from '@ant-design/icons'
import { actions as assignmentActions } from '../../../../../store/reducers/assignment.reducer'
import { connect } from 'react-redux'
import PatternIcon from '../../../../../components/common/PatternIcon'
import { PATTERN_TYPES } from '../../../../../constants/pattern-types'

const PatternFilters = ({ 
    assignment, 
    filters, 
    isWaitingForAssignmentInfo, 
    setPatternFilter, 
    setExactMatch, 
    setPatternExpanded,
    setPatternCriteriaFilter
}) => {
    const handlePatternFilterChange = patternId => {
        const _patterns = JSON.parse(JSON.stringify(filters.patterns))
        const _pattern = _patterns.find(pattern => pattern.id === patternId)
        _pattern.enabled = !_pattern.enabled                
        setPatternFilter(_patterns)
    }

    const handleExactMatch = event => {
        setExactMatch(event.target.checked)
    }

    const handleVisibilityChange = id => {
        const _patterns = JSON.parse(JSON.stringify(filters.patterns))
        const _pattern = _patterns.find(pattern => pattern.id === id)
        _pattern.show = !_pattern.show        
        setPatternFilter(_patterns)
    }
    
    const handlePatternExpand = id => {
        const pattern = filters.patterns.find(p => p.id === id)
        const newExpandedState = !pattern.expanded
        setPatternExpanded(id, newExpandedState)
    }
    
    const handleCriteriaFilterChange = (patternId, criteriaId) => {
        const pattern = filters.patterns.find(p => p.id === patternId)
        const criterion = pattern.criteria[criteriaId]
        const newEnabled = !criterion.enabled
        setPatternCriteriaFilter(patternId, criteriaId, newEnabled, criterion.show)
    }
    
    const handleCriteriaVisibilityChange = (patternId, criteriaId) => {
        const pattern = filters.patterns.find(p => p.id === patternId)
        const criterion = pattern.criteria[criteriaId]
        
        // Toggle the show state
        const newShow = !criterion.show
        
        // Update the criterion state
        setPatternCriteriaFilter(patternId, criteriaId, criterion.enabled, newShow)
        
        // No need to manually trigger filtering - the criteriaConditions memo will update
        // and the query will automatically refetch
    }

    let patternFilters

    if (isWaitingForAssignmentInfo) {
        patternFilters = <Loading />
    } else if (assignment?.patterns?.length) {
        patternFilters = (
            <div>
                <Checkbox checked={filters.exactMatch} onChange={handleExactMatch}>
                    Exact match (AND logic)
                </Checkbox>              
                <Divider style={{ margin: '8px 0' }} />
                <Space size={4} direction='vertical' style={{ width: '100%' }}>
                    {filters.patterns.map(pattern => {
                        const isSemanticPattern = pattern.type === PATTERN_TYPES.SEMANTIC.value
                        const hasCriteria = isSemanticPattern && pattern.criteria && pattern.criteria.length > 1
                        
                        return (
                            <div key={pattern.id}>
                                <Row align="middle">
                                    <Space size={8} align="center">
                                        <div style={{ width: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {hasCriteria && (
                                                <Button
                                                    type='text'
                                                    style={{ padding: 0, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={() => handlePatternExpand(pattern.id)}
                                                    aria-label={`${pattern.expanded ? 'Collapse' : 'Expand'} criteria for ${pattern.name} pattern`}
                                                    title={`${pattern.expanded ? 'Collapse' : 'Expand'} criteria for ${pattern.name} pattern`}
                                                >
                                                    {pattern.expanded ? <DownOutlined /> : <RightOutlined />}
                                                </Button>
                                            )}
                                        </div>
                                        <Button
                                            type='text'
                                            style={{ padding: 0, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            onClick={() => handleVisibilityChange(pattern.id)}
                                            aria-label={`${pattern.show ? 'Hide' : 'Show'} ${pattern.name} pattern when enabled`}
                                            title={`${pattern.show ? 'Hide' : 'Show'} ${pattern.name} pattern when enabled`}
                                        >
                                            {pattern.show ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                        </Button>
                                        <Checkbox
                                            checked={pattern.enabled}
                                            onChange={() => handlePatternFilterChange(pattern.id)}
                                            aria-label={`${pattern.enabled ? 'disable' : 'enable'} filtering by ${pattern.name} pattern`}
                                        >
                                            <Space size={8} align="center">
                                                <PatternIcon id={pattern.id} type={pattern.type} colorId={pattern.colorId} />
                                                {pattern.name}
                                            </Space>
                                        </Checkbox>
                                    </Space>
                                </Row>
                                
                                {/* Criteria checkboxes - outside of Checkbox.Group influence */}
                                {hasCriteria && pattern.expanded && (
                                    <div style={{ marginLeft: 32, marginTop: 4 }}>
                                        {pattern.criteria.map((criterion, index) => (
                                            <Row key={`${pattern.id}-criterion-${index}`} style={{ marginBottom: 4 }} align="middle">
                                                <Space size={8} align="center">
                                                    <Button
                                                        type='text'
                                                        style={{ padding: 0, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        onClick={() => handleCriteriaVisibilityChange(pattern.id, index)}
                                                        disabled={false}
                                                        aria-label={`${criterion.show ? 'Hide' : 'Show'} criterion when enabled`}
                                                        title={`${criterion.show ? 'Hide' : 'Show'} criterion when enabled`}
                                                    >
                                                        {criterion.show ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                    </Button>
                                                    <Checkbox
                                                        checked={criterion.enabled}
                                                        onChange={() => handleCriteriaFilterChange(pattern.id, index)}
                                                        disabled={false}
                                                        aria-label={`${criterion.enabled ? 'disable' : 'enable'} filtering by criterion`}
                                                        style={{ fontSize: '0.9em', lineHeight: '1.2' }}
                                                    >
                                                        {criterion.text}
                                                    </Checkbox>
                                                </Space>
                                            </Row>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </Space>
            </div>
        )
    } else {
        patternFilters = 'There are no patterns'
    }

    return <div>{patternFilters}</div>
}

const mapState = ({ assignment }) => {
    return {
        assignment: assignment.assignment,
        filters: assignment.filters,
        isWaitingForAssignmentInfo: assignment.isWaitingForAssignmentInfo,
    }
}

const mapDispatch = dispatch => {
    return {
        setExactMatch(value) {
            dispatch(assignmentActions.setExactMatch(value))
        },
        setPatternFilter(value) {
            dispatch(assignmentActions.setPatternFilter(value))
        },
        setPatternExpanded(patternId, expanded) {
            dispatch(assignmentActions.setPatternExpanded(patternId, expanded))
        },
        setPatternCriteriaFilter(patternId, criteriaId, enabled, show) {
            dispatch(assignmentActions.setPatternCriteriaFilter(patternId, criteriaId, enabled, show))
        }
    }
}

export default connect(mapState, mapDispatch)(PatternFilters)
